import { useField } from 'formik';
import { ON } from 'consts';

/**
 * useOnOffFormikField
 * -------------------
 * Centralises the common “is this ON?” logic for any Formik field that
 * stores the sentinel values defined in `consts` (e.g. ON = 'on').
 *
 * Params (single options object):
 *   fieldName      – *required* – full Formik path of the field.
 *   labelBase      – optional  – base string that will be decorated with
 *                                "(Enabled)" / "(Disabled)".
 *   onHelperText   – optional  – helper text when the field is ON.
 *   offHelperText  – optional  – helper text when the field is OFF.
 *   buildLabel     – optional  – function (base, isOn) => string
 *                                overrides the default label builder.
 *
 * Returns:
 *   {
 *     field,       // raw Formik field array[0]
 *     isOn,        // boolean
 *     label,       // string | undefined
 *     helperText,  // string | undefined
 *   }
 */
const useOnOffFormikField = ({
  fieldName,
  labelBase,
  onHelperText,
  offHelperText,
  buildLabel,
}) => {
  // Formik’s useField gives [field, meta, helpers]; we only need the first.
  const [field] = useField(fieldName);
  const isOn = field.value === ON;

  // If a custom label builder is provided, use it; otherwise, build a default label if labelBase is defined.
  const label =
    labelBase === undefined
      ? undefined
      : buildLabel
      ? buildLabel(labelBase, isOn)
      : labelBase;

  let helperText;
  if (onHelperText && offHelperText) {
    helperText = isOn ? onHelperText : offHelperText;
  }

  return { field, isOn, label, helperText };
};

export default useOnOffFormikField;
